import { Fragment, useCallback, useEffect, useState } from "react";

import { useAlert } from "@hl/base-components/lib/alert/useAlert";
import { logError } from "@hl/shared-features/lib/services/logger";
import { Button, Divider, Group, Space, Text, TextInput } from "@mantine/core";
import { omit } from "lodash";

import {
  Condition,
  ConditionType,
  GateCreateInput,
  GenericConditionData,
} from "apollo/graphql.generated";
import RemoveConditionModal from "~/features/gates/CreateOrEditGate/condition-modals/RemoveConditionModal";
import {
  GateDocument,
  GateQuery,
  GatesDocument,
  useGateCreateMutation,
  useGateUpdateMutation,
} from "~/features/gates/queries/gates.graphql.generated";

import AddConditionBox from "./AddConditionBox";
import AddConditionMenu from "./AddConditionMenu";
import AllAnyMenu from "./AllAnyMenu";
import SelectedConditions from "./SelectedConditions";
import SelectedMarketingAccessCard from "./SelectedMarketingAccessCard";
import { useConditionModals } from "./condition-modals";
import * as styles from "./styles";

export type CreateOrEditGateProps = {
  gate?: GateQuery["gate"];
  onSave?: (gateName: string) => void;
  onChange?: () => void;
  isModal?: boolean;
};

export const CreateOrEditGate = ({
  gate,
  onSave,
  onChange,
  isModal,
}: CreateOrEditGateProps) => {
  // Hooks
  const alert = useAlert();

  // Persistent state
  const [name, setName] = useState<string>("");
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [requireAll, setRequiredAll] = useState<boolean>(false);
  const [deleteConditionIndex, setDeleteConditionIndex] = useState<
    null | number
  >(null);

  const handleApplyConditionEdit = (
    condition: Condition,
    initialCondition?: Condition
  ) => {
    setConditions((prevConditions) => {
      return prevConditions.map((c) => {
        if (c === initialCondition) {
          return condition;
        }
        return c;
      });
    });

    onChange?.();
  };

  const {
    openSetAllowlistModal,
    openSetCurrencyModal,
    openFindTokenModal,
    openFindAttributeModal,
    openFindCollectionModal,
    openFarcasterModal,
  } = useConditionModals(handleApplyConditionEdit, false);

  // Set state on initial render (for edit gate)
  useEffect(() => {
    if (gate) {
      const conditions = gate.conditions ?? [];
      setName(gate.name ?? "");
      setConditions(conditions);
      setRequiredAll(
        conditions.map((condition) => condition.required).every((el) => el)
      );
    }
  }, [gate]);

  // State updates
  const addCondition = useCallback(
    (condition: Condition) => {
      if (condition?.type == null) {
        console.error("Condition is missing type", condition);
        return;
      }
      setConditions((prevConditions) => [
        ...prevConditions,
        {
          // TODO(james): this defaulting could happen elsewhere?
          amount: 1,
          type: condition.type,
          required: requireAll,
          ...condition,
        },
      ]);
      onChange?.();
    },
    [requireAll]
  );

  const handleRemoveCondition = (index: number) => {
    setConditions((prevConditions) =>
      prevConditions.filter((_, i) => i !== index)
    );
    onChange?.();
  };

  const handleEditCondition = (index: number) => {
    const initialCondition = conditions.at(index);
    if (initialCondition == null) return;

    switch (initialCondition.type) {
      case ConditionType.NFT:
      case ConditionType.COLLECTION:
        if (initialCondition.data?.tokenId == null) {
          openFindCollectionModal({
            initialCondition,
            showBackButton: isModal,
          });
        } else {
          openFindTokenModal({
            initialCondition,
            showBackButton: isModal,
          });
        }
        break;
      case ConditionType.ATTRIBUTE:
        openFindAttributeModal({
          initialCondition,
          showBackButton: isModal,
        });
        break;
      case ConditionType.ALLOWLIST:
        openSetAllowlistModal({
          initialCondition,
          showBackButton: isModal,
        });
        break;
      case ConditionType.CURRENCY:
        openSetCurrencyModal({
          initialCondition,
          showBackButton: isModal,
        });
        break;
      case ConditionType.FARCASTER:
        openFarcasterModal({
          initialCondition,
          showBackButton: isModal,
        });
        break;
      default:
        console.log("unknown condition");
    }
  };

  // Ephemeral state
  const [savingGate, setSavingGate] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    name: boolean;
    conditions: boolean;
    save: boolean;
  }>({ name: false, conditions: false, save: false });

  // Clear errors when user updates name or conditions
  useEffect(() => {
    setErrors({ name: false, conditions: false, save: false });
  }, [name, conditions]);

  // Persistence
  const [createGate] = useGateCreateMutation({
    refetchQueries: [GatesDocument],
  });
  const [updateGate] = useGateUpdateMutation({
    refetchQueries: [GateDocument, GatesDocument],
  });

  const persistGate = async (
    data: Pick<GateCreateInput, "name" | "conditions">
  ): Promise<string> => {
    try {
      if (gate) {
        const result = await updateGate({
          variables: { input: data, id: gate.id },
        });
        alert.success("Gate updated.");
        if (result.data) {
          return result.data.gateUpdate.id;
        } else {
          throw new Error("GateUpdate data missing");
        }
      } else {
        const result = await createGate({ variables: { input: data } });
        alert.success("Gate created.");
        if (result.data) {
          return result.data.gateCreate.id;
        } else {
          throw new Error("GateCreate data missing");
        }
      }
    } catch (cause) {
      const err = new Error("error persisting gate", { cause });
      logError(err);
      throw err;
    }
  };

  // Handle clicking Save
  const handleClickSave = useCallback(async () => {
    setSavingGate(true);
    if (!name || !conditions || !conditions.length) {
      setErrors({
        name: !name,
        conditions: !conditions || !conditions.length,
        save: false,
      });
      return;
    }
    // on edit, we're loading the full graphql object-response into local state,
    // and "__typename" sneaks in... do some cleanup via omitting
    const conditionsInput = conditions.map((condition) => {
      const attributes = (condition.data?.attributes ?? []).map((attr) => {
        return { ...omit(attr, "__typename") };
      });
      let data: GenericConditionData = {
        network: condition.data?.network,
        address: condition.data?.address,
        tokenId: condition.data?.tokenId,
        attributes:
          attributes && attributes.length > 0 ? attributes : undefined,
        allowlist: condition.data?.allowlist,
        allowlistRaw: condition.data?.allowlistRaw,
        email: condition.data?.email,
        followFID: condition.data?.followFID,
      };
      data = Object.fromEntries(
        Object.entries(data).filter(([_, v]) => v != null)
      );
      return {
        ...omit(condition, "__typename"),
        required: requireAll,
        data,
      };
    });
    try {
      const gateId = await persistGate({
        name,
        conditions: conditionsInput,
      });
      onSave?.(gateId);
    } catch (err) {
      setErrors({ ...errors, save: true });
      setSavingGate(false);
    }
  }, [name, conditions, errors, requireAll]);

  const marketingConditionIndex = conditions.findIndex(
    (c) => c.type === ConditionType.MARKETING_ACCESS
  );
  const requiresMarketingAccess = marketingConditionIndex > -1;
  return (
    <>
      <TextInput
        mb={0}
        size="md"
        placeholder="Only you can see this name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          onChange?.();
        }}
        label="Gate name"
        error={errors.name && "Name this gate to save this gate"}
      />
      <Space h={32} />
      <div css={styles.rowContainer}>
        <Text size="sm" component="label">
          Conditions
        </Text>
        {requiresMarketingAccess && conditions.length === 1 && (
          <>
            <Group align="center" spacing={6}>
              <Text size="sm">Someone must meet</Text>
              <AllAnyMenu
                requiredAll={requireAll}
                setRequiredAll={() => setRequiredAll(true)}
                setRequiredAny={() => setRequiredAll(false)}
              />
              <Text size="sm">of these conditions to pass this gate.</Text>
            </Group>
            <Space h="sm" />
            <SelectedMarketingAccessCard
              onRemoveCondition={() =>
                handleRemoveCondition(marketingConditionIndex)
              }
              email={true}
            />
          </>
        )}
        {requiresMarketingAccess && conditions.length > 1 && (
          <>
            <Group align="center" spacing={6}>
              <Text size="sm">
                Someone must provide a verified email address to pass this gate.
              </Text>
            </Group>
            <Space h="sm" />
            <SelectedMarketingAccessCard
              onRemoveCondition={() =>
                handleRemoveCondition(marketingConditionIndex)
              }
              email={true}
            />
            <Space h="sm" />
            <Group align="center" spacing={6}>
              <Text size="sm">In addition, they must meet</Text>
              <AllAnyMenu
                requiredAll={requireAll}
                setRequiredAll={() => setRequiredAll(true)}
                setRequiredAny={() => setRequiredAll(false)}
              />
              <Text size="sm">of these conditions to this gate.</Text>
            </Group>
          </>
        )}
        {!requiresMarketingAccess && (
          <Group align="center" spacing={6}>
            <Text size="sm">Someone must meet</Text>
            <AllAnyMenu
              requiredAll={requireAll}
              setRequiredAll={() => setRequiredAll(true)}
              setRequiredAny={() => setRequiredAll(false)}
            />
            <Text size="sm">of these conditions to pass this gate.</Text>
          </Group>
        )}
        <Space h={16} />

        {conditions.length === 0 ? (
          <AddConditionBox
            onAddCondition={addCondition}
            error={
              errors.conditions
                ? "Add at least one condition to save this gate"
                : ""
            }
            isModal={isModal}
            haveMarketingCondition={requiresMarketingAccess}
          />
        ) : (
          <>
            <SelectedConditions
              conditions={conditions}
              requireAll={requireAll}
              onRemoveCondition={(index) => {
                setDeleteConditionIndex(index);
              }}
              onEditCondition={handleEditCondition}
            />
            <Space h={40} />
            <AddConditionMenu
              onAddCondition={addCondition}
              isModal={isModal}
              haveMarketingCondition={requiresMarketingAccess}
            />
          </>
        )}
      </div>
      {deleteConditionIndex != null && (
        <RemoveConditionModal
          onClose={() => {
            setDeleteConditionIndex(null);
          }}
          onConfirm={() => {
            handleRemoveCondition(deleteConditionIndex);
            setDeleteConditionIndex(null);
          }}
        ></RemoveConditionModal>
      )}
      {isModal ? (
        <Divider
          my="md"
          sx={(theme) => ({
            marginRight: `-${theme.spacing.md}px`,
            marginLeft: `-${theme.spacing.md}px`,
          })}
        />
      ) : (
        <Space h={48} />
      )}
      <Button
        fullWidth
        size="xl"
        onClick={handleClickSave}
        loading={savingGate}
        disabled={!name || !conditions.length}
      >
        Save gate
      </Button>
    </>
  );
};
